import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import type { MeetingRecording } from '../../types';

interface UseDealClientFeedResult {
  clientActivities: any[];
  activitiesLoading: boolean;
  clientRecordings: MeetingRecording[];
}

/**
 * Carica notes (via store esterno), activities e meeting recordings
 * legate al cliente/lead selezionato di un'offerta. Refetch automatico
 * quando cambia `clientIdToFetch`.
 *
 * Le notes sono delegate al `useLeadNoteStore` (passato via `fetchNotes`)
 * perché vengono consumate anche fuori dal DealForm; le activities e i
 * recordings restano locali al form.
 */
export function useDealClientFeed(
  clientIdToFetch: string | undefined,
  fetchNotes: (clientId: string) => Promise<unknown>
): UseDealClientFeedResult {
  const [clientActivities, setClientActivities] = useState<any[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [clientRecordings, setClientRecordings] = useState<MeetingRecording[]>([]);

  useEffect(() => {
    const run = async () => {
      if (!clientIdToFetch) {
        setClientActivities([]);
        setClientRecordings([]);
        return;
      }

      // Notes via store (condiviso con altri componenti).
      // try/catch obbligatorio: senza, una rejection del fetch (race su unmount,
      // RLS, network) sale fuori dalla IIFE e diventa "Uncaught (in promise)".
      try {
        await fetchNotes(clientIdToFetch);
      } catch (err) {
        console.error('[useDealClientFeed] fetchNotes failed:', err);
      }

      // Activities
      setActivitiesLoading(true);
      try {
        const { data, error } = await supabase
          .from('sales_activities')
          .select(`
            *,
            users:user_profiles!sales_activities_user_id_fkey1(id, email, full_name, avatar_url, profile_image_url),
            assignee:user_profiles!sales_activities_assigned_to_fkey(id, email, full_name, avatar_url, profile_image_url)
          `)
          .or(`lead_id.eq.${clientIdToFetch},client_id.eq.${clientIdToFetch}`)
          .order('due_date', { ascending: false });

        if (!error) {
          setClientActivities(data || []);
        }
      } catch (err) {
        console.error('Error fetching activities:', err);
      } finally {
        setActivitiesLoading(false);
      }

      // Meeting recordings (lead_id e client_id condividono la tabella clients)
      try {
        const { data, error } = await supabase
          .from('meeting_recordings')
          .select('*')
          .or(`lead_id.eq.${clientIdToFetch},client_id.eq.${clientIdToFetch}`)
          .order('created_at', { ascending: false });

        if (!error && data) {
          const normalized = data.map(rec => ({
            ...rec,
            participants: rec.participants || [],
            transcript_segments: rec.transcript_segments || [],
            ai_key_points: rec.ai_key_points || [],
            ai_action_items: rec.ai_action_items || [],
          })) as MeetingRecording[];
          setClientRecordings(normalized);
        }
      } catch (err) {
        console.error('Error fetching recordings:', err);
      }
    };

    // .catch finale come safety net per qualunque rejection sfuggita ai
    // try/catch interni (es. throw sincrono prima del primo await).
    run().catch(err => console.error('[useDealClientFeed] run failed:', err));
  }, [clientIdToFetch, fetchNotes]);

  return { clientActivities, activitiesLoading, clientRecordings };
}
